// LikeDislikeButtons.js

import React, { useState, useEffect, useCallback, memo } from 'react'
import { supabase } from './supabaseClient'
import { IconButton, Stack } from '@mui/material'
import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined'
import ThumbDownIcon from '@mui/icons-material/ThumbDown'
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined'

/**
 * Reusable Like/Dislike Buttons component.
 *
 * Props:
 * - listingId: The ID of the listing
 * - onLikeDislikeChange: Optional callback when like/dislike status changes
 */
const LikeDislikeButtons = memo(({ listingId, onLikeDislikeChange }) => {
  // Local animation states
  const [animateLike, setAnimateLike] = useState(false)
  const [animateDislike, setAnimateDislike] = useState(false)
  // Track loading state to prevent multiple clicks
  const [isLoading, setIsLoading] = useState(false)
  const [engagmentId, setEngagmentId] = useState(null)
  const [likedStatus, setLikedStatus] = useState(null)

  const fetchEngagementStatus = useCallback(async () => {
    const { data, error } = await supabase
      .from('listing_engagements')
      .select('id, liked')
      .eq('listing_id', listingId)

    if (error) {
      console.error('Error fetching engagement status:', error)
    } else {
      setLikedStatus(data[0]?.liked)
      setEngagmentId(data[0]?.id)
    }
  }, [listingId])

  useEffect(() => {
    fetchEngagementStatus()
  }, [fetchEngagementStatus])

  // Update engagement in Supabase - extracted as a reusable function
  const updateEngagement = useCallback(
    async (newValue) => {
      const { error } = await supabase.from('listing_engagements').upsert(
        {
          id: engagmentId,
          listing_id: listingId,
          liked: newValue,
        },
        { onConflict: ['listing_id', 'user_id'] }
      )
      if (error) {
        console.error('Error updating engagement:', error)
        return false
      }

      return true
    },
    [engagmentId, listingId]
  )

  // Toggle Like in Supabase
  const handleLike = useCallback(async () => {
    if (!listingId || isLoading) return

    setIsLoading(true)
    try {
      const newValue = likedStatus === true ? null : true
      const success = await updateEngagement(newValue)
      if (!success) {
        setIsLoading(false)
        return
      }

      // Update local state immediately
      setLikedStatus(newValue)

      // Animate
      setAnimateLike(true)
      setTimeout(() => setAnimateLike(false), 300)

      // Notify parent so it can refresh data or reorder
      if (onLikeDislikeChange) {
        onLikeDislikeChange(listingId, newValue)
      }
    } catch (err) {
      console.error('Unexpected error in handleLike:', err)
    } finally {
      setIsLoading(false)
    }
  }, [listingId, likedStatus, isLoading, updateEngagement, onLikeDislikeChange])

  // Toggle Dislike in Supabase
  const handleDislike = useCallback(async () => {
    if (!listingId || isLoading) return

    setIsLoading(true)
    try {
      const newValue = likedStatus === false ? null : false
      const success = await updateEngagement(newValue)
      if (!success) {
        setIsLoading(false)
        return
      }

      // Update local state immediately
      setLikedStatus(newValue)

      // Animate
      setAnimateDislike(true)
      setTimeout(() => setAnimateDislike(false), 300)

      // Notify parent so it can refresh data or reorder
      if (onLikeDislikeChange) {
        onLikeDislikeChange(listingId, newValue)
      }
    } catch (err) {
      console.error('Unexpected error in handleDislike:', err)
    } finally {
      setIsLoading(false)
    }
  }, [listingId, likedStatus, isLoading, updateEngagement, onLikeDislikeChange])

  // Prevent rendering if listing is missing
  if (!listingId) {
    return null
  }

  return (
    <Stack direction="row" spacing={1}>
      <IconButton onClick={handleLike} aria-label="like" disabled={isLoading}>
        {likedStatus === true ? (
          <ThumbUpIcon
            color="primary"
            sx={{
              transition: 'transform 0.3s ease',
              transform: animateLike ? 'scale(1.2)' : 'scale(1)',
            }}
          />
        ) : (
          <ThumbUpOutlinedIcon
            sx={{
              transition: 'transform 0.3s ease',
              transform: animateLike ? 'scale(1.2)' : 'scale(1)',
            }}
          />
        )}
      </IconButton>

      <IconButton onClick={handleDislike} aria-label="dislike" disabled={isLoading}>
        {likedStatus === false ? (
          <ThumbDownIcon
            color="error"
            sx={{
              transition: 'transform 0.3s ease',
              transform: animateDislike ? 'scale(1.2)' : 'scale(1)',
            }}
          />
        ) : (
          <ThumbDownOutlinedIcon
            sx={{
              transition: 'transform 0.3s ease',
              transform: animateDislike ? 'scale(1.2)' : 'scale(1)',
            }}
          />
        )}
      </IconButton>
    </Stack>
  )
})

// Add display name for better debugging
LikeDislikeButtons.displayName = 'LikeDislikeButtons'

export default LikeDislikeButtons
